import { useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Text } from '@/components/ui/Text'
import { Minus, Plus } from 'lucide-react'
import { CreditIcon } from '@/components/icons/CreditIcon'
import { getPlanCard, getOverageRateForPlan } from './planConfig'
import type { BillingInterval } from '@/lib/api'
import { MARKETPLACE_RATE_FOOTNOTE, PRIVATE_CREDIT_RATES } from '@/lib/billing/creditRateTable'
import { useTranslation } from 'react-i18next'

// ─── rates ───────────────────────────────────────────────────────────────────

// Every row prices off the same derived table the credits dialog renders, so
// the two surfaces can never quote different numbers and no rate is ever
// inlined here as a literal.
function rateFor(action: string): number {
  const row = PRIVATE_CREDIT_RATES.find((r) => r.action === action)
  if (!row) {
    throw new Error(`Rate missing from PRIVATE_CREDIT_RATES: ${action}`)
  }
  return row.rate
}

const STUDY_RATE = rateFor('Study')
const AUDIENCE_RATE = rateFor('Audience creation')
const QUICK_STUDY_RATE = rateFor('Quick study')
const CHAT_RATE = rateFor('Chat')

// ─── types ───────────────────────────────────────────────────────────────────

export interface CreditScenarioModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * The org's real billing interval, so an annual customer sees the overage
   * rate they are actually charged. Absent (prospects), monthly applies.
   */
  interval?: BillingInterval | null
}

// ─── inline editable number ──────────────────────────────────────────────────

interface EditableNumProps {
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step?: number
  suffix?: string
  testId?: string
}

function EditableNum({ value, onChange, min, max, step = 1, suffix, testId }: EditableNumProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  function commitEdit() {
    setEditing(false)
    const n = parseInt(draft, 10)
    if (!Number.isNaN(n)) {
      onChange(Math.min(max, Math.max(min, n)))
    }
  }

  return (
    <span className="inline-flex items-center gap-0 group relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - step))}
        disabled={value <= min}
        className="w-5 h-5 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-all opacity-40 enabled:group-hover:opacity-100 enabled:group-focus-within:opacity-100 disabled:opacity-0"
      >
        <Minus className="h-3 w-3" />
      </button>
      <span className="relative inline-flex items-center justify-center min-w-[1.5ch]">
        {/* Invisible sizer to hold width stable */}
        <span className="font-display text-2xl font-normal tabular-nums invisible whitespace-nowrap">
          {value.toLocaleString()}
        </span>
        {editing ? (
          <input
            type="number"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => { if (e.key === 'Enter') commitEdit() }}
            min={min}
            max={max}
            step={step}
            className="absolute inset-0 font-display text-2xl font-normal tabular-nums text-foreground bg-transparent border-b border-primary outline-none text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none w-full"
          />
        ) : (
          <span
            className="absolute inset-0 flex items-center justify-center font-display text-2xl font-normal tabular-nums text-foreground cursor-text border-b border-dashed border-muted-foreground/40 hover:border-solid hover:border-muted-foreground"
            onClick={() => { setDraft(String(value)); setEditing(true) }}
          >
            {value.toLocaleString()}
          </span>
        )}
      </span>
      {suffix && <span className="text-sm text-muted-foreground ml-0.5">{suffix}</span>}
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + step))}
        disabled={value >= max}
        className="w-5 h-5 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-all opacity-40 enabled:group-hover:opacity-100 enabled:group-focus-within:opacity-100 disabled:opacity-0"
      >
        <Plus className="h-3 w-3" />
      </button>
    </span>
  )
}

// ─── modal ───────────────────────────────────────────────────────────────────

export function CreditScenarioModal({
  open,
  onOpenChange,
  interval,
}: CreditScenarioModalProps) {
  const { t } = useTranslation('app')
  const [studies, setStudies] = useState(2)
  const [sampleSize, setSampleSize] = useState(100)
  const [audiences, setAudiences] = useState(1)
  const [audienceSize, setAudienceSize] = useState(500)
  const [quickStudies, setQuickStudies] = useState(4)
  const [quickQuestions, setQuickQuestions] = useState(10)
  const [quickTwins, setQuickTwins] = useState(100)
  const [chatSessions, setChatSessions] = useState(10)
  const [msgsPerSession, setMsgsPerSession] = useState(10)

  // The modal always computes for the Enterprise plan, the only one now
  // sold. A card with no public price (Enterprise is "Custom") must not fall
  // back to the catalog dollar amount.
  const planConfig = getPlanCard('enterprise')
  const includedCredits = planConfig.includedCredits ?? 0
  const planLabel = planConfig.displayName
  const planMonthlyUsd = planConfig.monthlyPriceUsd
  const overageRateCents =
    planMonthlyUsd === null
      ? 0
      : getOverageRateForPlan('enterprise', interval ?? 'monthly')

  const breakdown = useMemo(() => {
    const studyCredits = studies * sampleSize * STUDY_RATE
    const audienceCredits = audiences * audienceSize * AUDIENCE_RATE
    // Rounded here, before anything downstream reads it: a typed value can
    // bypass the twins stepper's step of 10 (3 questions × 11 twins lands on
    // 9.9), and the total, overage and overage cost must never see a float.
    const quickStudyCredits = Math.round(quickStudies * quickQuestions * quickTwins * QUICK_STUDY_RATE)
    const chatCredits = chatSessions * msgsPerSession * CHAT_RATE
    const total = studyCredits + audienceCredits + quickStudyCredits + chatCredits
    const overage = Math.max(0, total - includedCredits)
    const overageCost = overageRateCents > 0 ? (overage * overageRateCents) / 100 : 0
    const totalCost = planMonthlyUsd === null ? null : planMonthlyUsd + overageCost
    return { studyCredits, audienceCredits, quickStudyCredits, chatCredits, total, overage, overageCost, totalCost }
  }, [studies, sampleSize, audiences, audienceSize, quickStudies, quickQuestions, quickTwins,
      chatSessions, msgsPerSession, includedCredits, overageRateCents, planMonthlyUsd])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-card border-border max-w-[600px] rounded-2xl p-0 gap-0"
        data-testid="credit-scenario-modal"
      >
        <div className="px-8 py-7">

          {/* Header line */}
          <DialogTitle className="font-display text-2xl font-normal text-foreground tracking-tight mb-1.5">
            {t('billing.estimateUsage')}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mb-6">
            Set your expected activity to see credits used
          </DialogDescription>

          {/* Receipt rows */}
          <div className="space-y-0">

            {/* Studies */}
            <div className="flex items-baseline justify-between py-4 border-b border-border">
              <div className="flex items-baseline gap-1 flex-wrap">
                <EditableNum value={studies} onChange={setStudies} min={1} max={20} />
                <span className="text-sm text-muted-foreground">studies ×</span>
                <EditableNum value={sampleSize} onChange={setSampleSize} min={50} max={2000} step={50} />
                <span className="text-sm text-muted-foreground">twins</span>
                <span className="text-xs text-muted-foreground ml-1 inline-flex items-center gap-0.5"><CreditIcon className="h-3 w-3" />{STUDY_RATE}/twin</span>
              </div>
              <span className="font-display text-2xl tabular-nums text-foreground font-normal shrink-0 ml-4">
                {breakdown.studyCredits.toLocaleString()}
              </span>
            </div>

            {/* Audiences */}
            <div className="flex items-baseline justify-between py-4 border-b border-border">
              <div className="flex items-baseline gap-1 flex-wrap">
                <EditableNum value={audiences} onChange={setAudiences} min={0} max={10} />
                <span className="text-sm text-muted-foreground">audiences ×</span>
                <EditableNum value={audienceSize} onChange={setAudienceSize} min={50} max={5000} step={50} />
                <span className="text-sm text-muted-foreground">twins</span>
                <span className="text-xs text-muted-foreground ml-1 inline-flex items-center gap-0.5"><CreditIcon className="h-3 w-3" />{AUDIENCE_RATE}/twin</span>
              </div>
              <span className="font-display text-2xl tabular-nums text-foreground font-normal shrink-0 ml-4">
                {breakdown.audienceCredits.toLocaleString()}
              </span>
            </div>

            {/* Quick studies. Twins step by 10 so 0.3 × twins stays whole, which
                keeps this row equal to the real per-question charge. */}
            <div className="flex items-baseline justify-between py-4 border-b border-border">
              <div className="flex items-baseline gap-1 flex-wrap">
                <EditableNum value={quickStudies} onChange={setQuickStudies} min={0} max={20} testId="credit-scenario-quick-studies" />
                <span className="text-sm text-muted-foreground">quick studies ×</span>
                <EditableNum value={quickQuestions} onChange={setQuickQuestions} min={1} max={30} testId="credit-scenario-quick-questions" />
                <span className="text-sm text-muted-foreground">questions ×</span>
                <EditableNum value={quickTwins} onChange={setQuickTwins} min={10} max={2000} step={10} testId="credit-scenario-quick-twins" />
                <span className="text-sm text-muted-foreground">twins</span>
                <span className="text-xs text-muted-foreground ml-1 inline-flex items-center gap-0.5"><CreditIcon className="h-3 w-3" />{QUICK_STUDY_RATE}/twin/question</span>
              </div>
              <span
                className="font-display text-2xl tabular-nums text-foreground font-normal shrink-0 ml-4"
                data-testid="credit-scenario-quick-study-credits"
              >
                {breakdown.quickStudyCredits.toLocaleString()}
              </span>
            </div>

            {/* Chat */}
            <div className="flex items-baseline justify-between py-4 border-b border-border">
              <div className="flex items-baseline gap-1 flex-wrap">
                <EditableNum value={chatSessions} onChange={setChatSessions} min={0} max={50} />
                <span className="text-sm text-muted-foreground">chats ×</span>
                <EditableNum value={msgsPerSession} onChange={setMsgsPerSession} min={1} max={30} />
                <span className="text-sm text-muted-foreground">msgs</span>
                <span className="text-xs text-muted-foreground ml-1 inline-flex items-center gap-0.5"><CreditIcon className="h-3 w-3" />{CHAT_RATE}/msg</span>
              </div>
              <span className="font-display text-2xl tabular-nums text-foreground font-normal shrink-0 ml-4">
                {breakdown.chatCredits.toLocaleString()}
              </span>
            </div>

          </div>

          {/* Totals: right aligned */}
          <div className="mt-5 flex flex-col items-end gap-1.5">
            <div className="flex items-baseline gap-6">
              <span className="text-sm text-muted-foreground">Credits</span>
              <span className="font-display text-xl tabular-nums text-foreground font-normal" data-testid="credit-scenario-total">
                {breakdown.total.toLocaleString()}
              </span>
            </div>
            {planMonthlyUsd !== null && breakdown.totalCost !== null && (
              <>
                <div className="flex items-baseline gap-6" data-testid="credit-scenario-plan-price">
                  <span className="text-sm text-muted-foreground">{planLabel} plan</span>
                  <span className="font-display text-xl tabular-nums text-foreground font-normal">
                    ${planMonthlyUsd.toLocaleString()}/month
                  </span>
                </div>
                {breakdown.overage > 0 && overageRateCents > 0 && (
                  <div className="flex items-baseline gap-6">
                    <span className="text-sm text-muted-foreground">Overage ({breakdown.overage.toLocaleString()} cr)</span>
                    <span className="font-display text-xl tabular-nums text-foreground font-normal">
                      ~${breakdown.overageCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                <div className="w-32 h-px bg-border mt-1 mb-1" />
                <div className="flex items-baseline gap-6">
                  <span className="text-sm font-medium text-foreground">Est. total</span>
                  <span className="font-display text-2xl tabular-nums text-foreground font-medium" data-testid="credit-scenario-est-total">
                    ~${Math.round(breakdown.totalCost).toLocaleString()}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">/month</span>
              </>
            )}
          </div>

          {/* Quick Study cost scales with reads (twins × questions × creatives), so
              the row above prices one creative per question; monadic fan-outs
              multiply it. */}
          <Text size="xs" color="muted" className="mt-6 leading-relaxed">
            *{MARKETPLACE_RATE_FOOTNOTE}
          </Text>

        </div>
      </DialogContent>
    </Dialog>
  )
}
